import {
  Box,
  Button,
  VStack,
  Stack,
  Text,
  Icon,
  Badge,
  Separator,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useState } from "react";

import { useUserContext } from "../../../Contexts/userContext";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import AppUserForm from "../Settings/UserSettings/AppUserForm";
import UserImage from "../Settings/UserSettings/UserImage";
import ChangePasswordModal from "../Settings/UserSettings/ChangePasswordModal";
import useToaster from "../../../hooks/useToaster";
import useAxios from "../../../hooks/useAxios";
import useIcons from "../../../hooks/useIcons";
import useThemeColors from "../../../hooks/useThemeColors";
import Cad from "../../CustomUi/Cad";
import SectionHeader from "../../CustomUi/SectionHeader";
import {
  validateEmail,
  validateGender,
  validateRequired,
} from "../../../services/validation";
import { AppUser, ApiDataResponse } from "../../../services/Dtos";
import CustomAlert from "../../CustomUi/CustomAlert";

function Profile() {
  const axiosClient = useAxios();
  const { user, setUser } = useUserContext();
  const { accentColor } = useSchoolsContext();
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const { showErrorToast, showSuccessToast } = useToaster();
  const icons = useIcons();
  const colors = useThemeColors();

  const initialProfileState: AppUser = {
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    role: 0,
    token: "",
    schoolId: "",
    gender: 0,
    isActive: false,
    avatarUrl: "",
    createdAt: "",
    updatedAt: "",
    isAccountActivated: false,
    accountActivatedAt: "",
  };

  const [profileFormState, setProfileFormState] =
    useState<AppUser>(initialProfileState);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (user) {
      setProfileFormState(user);

    } else {
      setProfileFormState(initialProfileState);
    }
  }, [user]);

  const validateForm = (): boolean => {
    const currentErrors: { [key: string]: string } = {};

    if (!validateRequired(profileFormState.firstName)) {
      currentErrors.firstName = "First Name is required";
    }
    if (!validateRequired(profileFormState.lastName)) {
      currentErrors.lastName = "Last Name is required";
    }
    if (!validateGender(profileFormState.gender)) {
      currentErrors.gender = "Gender is required";
    }
    if (!validateEmail(profileFormState.email)) {
      currentErrors.email = "Valid email is required";
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleProfileChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProfileFormState((prevState) => ({
      ...prevState,
      [name]: name === "gender" ? parseInt(value, 10) : value,
    }));
  };

  const handleToggleEdit = () => {
    setEditingProfile((prev) => !prev);
    if (editingProfile) {
      // Reset form state when canceling
      setProfileFormState(user || initialProfileState);
      setProfileImage(null);
      setErrors({});
    }
  };

  const handleImageUpload = async (image: string | null): Promise<boolean> => {
    if (!image || !user) return false;

    const blob = await fetch(image).then((res) => res.blob());
    const formData = new FormData();
    formData.append("file", blob);

    try {
      const response = await axiosClient.post<{
        data: string;
        succeeded: boolean;
      }>(`users/uploaduserimage/${user.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.succeeded) {
        const newAvatarUrl = response.data.data;
        setUser((prev) => {
          if (prev) {
            const updatedUser = { ...prev, avatarUrl: newAvatarUrl };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            return updatedUser;
          }
          return prev;
        });
        return true;
      }
    } catch (error) {
    }
    return false;
  };

  const handleProfileUpdate = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const response = await axiosClient.put<ApiDataResponse<AppUser>>(
        `users/${user.id}`,
        profileFormState
      );

      if (response.data.succeeded) {
        // Use the server response if it includes updated user data
        const updatedUser = response.data.data || {
          ...profileFormState,
          updatedAt: new Date().toISOString(),
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return true;
      }
    } catch (error) {
    }
    return false;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    let imageUploadSuccess = true;
    let profileUpdateSuccess = true;

    // Handle image upload if there's a new image
    if (profileImage) {
      imageUploadSuccess = await handleImageUpload(profileImage);
      if (!imageUploadSuccess) {
        showErrorToast("Failed to upload image");
        setIsLoading(false);
        return;
      }
    }

    // Handle profile update if there are changes
    if (user && JSON.stringify(user) !== JSON.stringify(profileFormState)) {
      profileUpdateSuccess = await handleProfileUpdate();
      if (!profileUpdateSuccess) {
        showErrorToast("Failed to update profile");
        setIsLoading(false);
        return;
      }
    }

    if (imageUploadSuccess && profileUpdateSuccess) {
      showSuccessToast("Profile updated successfully");
      setEditingProfile(false);
      setProfileImage(null);
    }

    setIsLoading(false);
  };

  const getRoleDisplayName = (role: number): string => {
    switch (role) {
      case 0:
        return "Viewer";
      case 1:
        return "Teacher";
      case 2:
        return "Editor";
      case 3:
        return "Administrator";
      default:
        return "Unknown";
    }
  };

  const getGenderDisplayName = (gender: number): string => {
    return gender === 1 ? "Male" : "Female";
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!user) {
    return <CustomAlert message="User information not available" />;
  }

  return (
    <VStack gap={6} align="stretch">
      {/* Profile Header */}
      <Cad>
        <VStack gap={{ base: 4, md: 6 }} align="stretch">
          <SectionHeader
            title="My Profile"
            buttonText={editingProfile ? "Cancel" : "Edit Profile"}
            onButtonClick={handleToggleEdit}
            buttonIcon={
              editingProfile ? icons.utility.undo : icons.utility.edit
            }
            buttonVariant="outline"
            icon={icons.people.user}
          />

          <Stack
            direction={{ base: "column", lg: "row" }}
            gap={{ base: 6, lg: 8 }}
            align="flex-start"
          >
            {/* Profile Image Section */}
            <VStack gap={4} align="center" w={{ base: "full", lg: "auto" }}>
              <UserImage
                media={profileImage}
                setMedia={setProfileImage}
                isEditing={editingProfile}
                handleUpload={() => handleImageUpload(profileImage)}
              />

              {!editingProfile && (
                <VStack gap={2} align="center" textAlign="center">
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    color={colors.text.secondary}
                    px={2}
                  >
                    Member since {formatDate(user.createdAt)}
                  </Text>
                </VStack>
              )}
            </VStack>

            {/* Profile Information Section */}
            <Box
              flex={1}
              w={{ base: "full", lg: "auto" }}
              minW={{ lg: "300px" }}
            >
              {editingProfile ? (
                <VStack gap={{ base: 4, md: 6 }} align="stretch">
                  <AppUserForm
                    onUserChange={handleProfileChange}
                    appUserFormState={profileFormState}
                    errors={errors}
                  />

                  <VStack
                    gap={{ base: 2, md: 3 }}
                    align={{ base: "stretch", md: "start" }}
                    flexDirection={{ base: "column", md: "row" }}
                  >
                    <Button
                      loading={isLoading}
                      onClick={handleSave}
                      colorPalette={accentColor}
                      w={{ base: "full", md: "auto" }}
                    >
                      <Icon as={icons.utility.save} mr={2} />
                      Save Changes
                    </Button>
                    <Button
                      onClick={() => setOpenChangePasswordModal(true)}
                      variant="outline"
                      w={{ base: "full", md: "auto" }}
                    >
                      <Icon as={icons.utility.shield} mr={2} />
                      Change Password
                    </Button>
                  </VStack>
                </VStack>
              ) : (
                <VStack gap={{ base: 4, md: 6 }} align="stretch">
                  {/* Personal Information */}
                  <Box>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="semibold"
                      color={colors.text.primary}
                      mb={{ base: 3, md: 4 }}
                    >
                      Personal Information
                    </Text>
                    <VStack gap={{ base: 3, md: 4 }} align="stretch">
                      <Box>
                        <Text
                          color={colors.text.secondary}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          Full Name
                        </Text>
                        <Text
                          fontWeight="medium"
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {user.firstName} {user.lastName}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          color={colors.text.secondary}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          Email
                        </Text>
                        <Text
                          fontWeight="medium"
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {user.email}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          color={colors.text.secondary}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          Gender
                        </Text>
                        <Text
                          fontWeight="medium"
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {getGenderDisplayName(user.gender)}
                        </Text>
                      </Box>
                    </VStack>
                  </Box>

                  <Separator />

                  {/* Account Information */}
                  <Box>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="semibold"
                      color={colors.text.primary}
                      mb={{ base: 3, md: 4 }}
                    >
                      Account Information
                    </Text>
                    <VStack gap={{ base: 3, md: 4 }} align="stretch">
                      <Box>
                        <Text
                          color={colors.text.secondary}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          Role
                        </Text>
                        <Badge
                          colorPalette={accentColor}
                          variant="subtle"
                          px={3}
                          py={1}
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {getRoleDisplayName(user.role)}
                        </Badge>
                      </Box>
                      <Box>
                        <Text
                          color={colors.text.secondary}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          Status
                        </Text>
                        <Badge
                          colorPalette={
                            user.isAccountActivated ? "green" : "red"
                          }
                          variant="subtle"
                          px={3}
                          py={1}
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          {user.isAccountActivated ? "Active" : "Inactive"}
                        </Badge>
                      </Box>
                      <Box>
                        <Text
                          color={colors.text.secondary}
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={1}
                        >
                          Member Since
                        </Text>
                        <Text
                          fontWeight="medium"
                          fontSize={{ base: "sm", md: "md" }}
                        >
                          {formatDate(user.createdAt)}
                        </Text>
                      </Box>
                      {user.updatedAt !== user.createdAt &&
                        user.updatedAt !== "0001-01-01T00:00:00.000Z" && (
                          <Box>
                            <Text
                              color={colors.text.secondary}
                              fontSize={{ base: "xs", md: "sm" }}
                              mb={1}
                            >
                              Last Updated
                            </Text>
                            <Text
                              fontWeight="medium"
                              fontSize={{ base: "sm", md: "md" }}
                            >
                              {formatDate(user.updatedAt)}
                            </Text>
                          </Box>
                        )}
                    </VStack>
                  </Box>

                  <Separator />

                  {/* Quick Actions */}
                  <Box>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="semibold"
                      color={colors.text.primary}
                      mb={{ base: 3, md: 4 }}
                    >
                      Quick Actions
                    </Text>
                    <VStack
                      gap={{ base: 2, md: 3 }}
                      align={{ base: "stretch", md: "start" }}
                    >
                      <Button
                        variant="outline"
                        onClick={() => setOpenChangePasswordModal(true)}
                        w={{ base: "full", md: "auto" }}
                        size="sm"
                      >
                        <Icon as={icons.utility.shield} />
                        Change Password
                      </Button>
                    </VStack>
                  </Box>
                </VStack>
              )}
            </Box>
          </Stack>
        </VStack>
      </Cad>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={openChangePasswordModal}
        onClose={() => setOpenChangePasswordModal(false)}
      />
    </VStack>
  );
}

export default Profile;
